import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Plus, Minus, Check, Share2, Copy, Loader2, ShoppingCart, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuthStore } from '@/store';
import axios from 'axios';

import { API } from '@/config/api';
import { copyText } from '@/lib/copyText';

export default function GroupOrderPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuthStore();
  
  const [groupOrder, setGroupOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState(searchParams.get('join') || '');
  
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroupOrder = useLatestCallback(async () => {
    try {
      const res = await axios.get(`${API}/group-order/${code}`, { headers });
      setGroupOrder(res.data);
      
      // Fetch restaurant and menu
      const [restRes, menuRes] = await Promise.all([
        axios.get(`${API}/restaurants/${res.data.restaurant_id}`),
        axios.get(`${API}/restaurants/${res.data.restaurant_id}/menu`)
      ]);
      setRestaurant(restRes.data);
      setMenu(menuRes.data);
    } catch (error) {
      toast.error('Group order not found or expired');
      navigate('/group-order');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=/group-order');
      return;
    }
    
    if (code) {
      fetchGroupOrder();
    } else {
      setLoading(false);
    }
  }, [code, isAuthenticated, navigate, fetchGroupOrder]);

  const createGroupOrder = async (restaurantId) => {
    try {
      const res = await axios.post(`${API}/group-order/create`, 
        { restaurant_id: restaurantId },
        { headers }
      );
      navigate(`/group-order/${res.data.code}`);
    } catch (error) {
      toast.error('Failed to create group order');
    }
  };

  const joinGroupOrder = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      await axios.post(`${API}/group-order/join`, 
        { code: joinCode.toUpperCase() },
        { headers }
      );
      navigate(`/group-order/${joinCode.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join group order');
    } finally {
      setJoining(false);
    }
  };

  const addItem = async (menuItem) => {
    try {
      await axios.post(`${API}/group-order/${code}/items`, 
        { menu_item_id: menuItem.id, quantity: 1 },
        { headers }
      );
      toast.success(`Added ${menuItem.name}`);
      fetchGroupOrder();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/group-order/${code}/items/${itemId}`, { headers });
      fetchGroupOrder();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const markReady = async () => {
    try {
      await axios.put(`${API}/group-order/${code}/ready`, {}, { headers });
      toast.success('Marked as ready!');
      fetchGroupOrder();
    } catch (error) {
      toast.error('Failed to mark ready');
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/group-order?join=${code}`;
    copyText(link, 'Link copied to clipboard!');
  };

  const checkout = async () => {
    try {
      const res = await axios.post(`${API}/group-order/${code}/checkout?delivery_address=123 Main St&order_type=delivery`, {}, { headers });
      toast.success('Order placed!');
      navigate(`/orders/${res.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  // Join/Create page
  if (!code) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 text-center">Group Order</h1>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Join Group Order
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Enter group code (e.g., ABC123)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                maxLength={6}
              />
              <Button onClick={joinGroupOrder} disabled={joining || !joinCode} className="w-full">
                {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" /> Start New Group Order
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Choose a restaurant to start a new group order
              </p>
              <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                Browse Restaurants
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Group Order
          </h1>
          <p className="text-muted-foreground">{restaurant?.name}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="text-lg font-mono">{code}</Badge>
            <Button variant="ghost" size="icon" onClick={copyShareLink}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={copyShareLink}>
            <Share2 className="h-4 w-4 mr-2" /> Share Link
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Menu */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-semibold">Menu</h2>
          {menu.map((category) => (
            <div key={category.id}>
              <h3 className="font-medium text-muted-foreground mb-2">{category.name}</h3>
              <div className="grid gap-2">
                {category.items.map((item) => (
                  <Card key={item.id} className="overflow-hidden">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                      </div>
                      <Button size="sm" onClick={() => addItem(item)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Participants */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Participants ({groupOrder?.participants?.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {groupOrder?.participants?.map((p) => (
                <div key={p.id} className="flex items-center justify-between">
                  <span>{p.name} {p.id === groupOrder.host_id && '(Host)'}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{p.item_count} items</Badge>
                    {p.is_ready && <Check className="h-4 w-4 text-green-500" />}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Cart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" /> Group Cart
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {groupOrder?.items?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No items yet</p>
              ) : (
                groupOrder?.items?.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p>{item.quantity}x {item.name}</p>
                      <p className="text-xs text-muted-foreground">by {item.participant_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>${item.total_price.toFixed(2)}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(item.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
              <div className="border-t pt-3 flex justify-between font-semibold">
                <span>Subtotal</span>
                <span>${groupOrder?.subtotal?.toFixed(2)}</span>
              </div>
              
              {!groupOrder?.is_host ? (
                <Button onClick={markReady} className="w-full" variant="outline">
                  <Check className="h-4 w-4 mr-2" /> I'm Ready
                </Button>
              ) : (
                <Button onClick={checkout} className="w-full" disabled={groupOrder?.items?.length === 0}>
                  Checkout Group Order
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}